import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

// Komai redesign: bold/confident, flat (no shadows), brand-only colors.
const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-semibold transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-indigo disabled:pointer-events-none disabled:opacity-40',
  {
    variants: {
      variant: {
        // Primary — solid indigo, cream text
        default:
          'bg-brand-indigo text-brand-cream hover:bg-brand-indigo-hover',
        // Secondary — solid teal, indigo text (best contrast within palette)
        secondary:
          'bg-brand-teal text-brand-indigo hover:bg-brand-teal-soft',
        // Tertiary / outline — indigo border + indigo text
        outline:
          'border-2 border-brand-indigo bg-transparent text-brand-indigo hover:bg-brand-indigo hover:text-brand-cream',
        // Ghost — minimal, indigo text only
        ghost:
          'bg-transparent text-brand-indigo hover:bg-brand-indigo/10',
        // Destructive — outlined indigo (no red allowed)
        destructive:
          'border-2 border-brand-indigo bg-brand-cream text-brand-indigo hover:bg-brand-indigo hover:text-brand-cream',
        // Link — text-only with underline on hover
        link: 'text-brand-indigo underline-offset-4 hover:underline',
        // Legacy aliases (mapped to new variants)
        black: 'bg-brand-indigo text-brand-cream hover:bg-brand-indigo-hover',
        dark: 'bg-brand-indigo text-brand-cream hover:bg-brand-indigo-hover',
        light:
          'border-2 border-brand-indigo bg-brand-cream text-brand-indigo hover:bg-brand-indigo hover:text-brand-cream',
        onboarding:
          'bg-brand-teal text-brand-indigo hover:bg-brand-teal-soft font-bold px-4',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-11 rounded-lg px-6 text-sm',
        xl: 'h-12 rounded-xl px-8 text-base',
        icon: 'h-10 w-10',
        onboarding: 'h-12 text-sm',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
